import { useEffect, useState } from 'react';
import { BOOT_FADE_MS, BOOT_MIN_MS } from './loader-clock';

export type BootPhase = 'holding' | 'leaving' | 'done';

const prefersStill = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * HOW LONG A COLD LOAD SHOWS THE LOADER, AND HOW IT LEAVES.
 *
 * `holding`: show the loader and nothing else, until BOTH the session is known (`waiting` is
 * false) and `BOOT_MIN_MS` has passed since the navigation began. A load that is already slower
 * than that waits for nothing extra.
 * `leaving`: the page is drawn and the loader fades over it (`BOOT_FADE_MS`), still looping.
 * `done`: the loader is gone.
 *
 * A reload from cache puts the app up in about a tenth of a second, and the loader used to go with
 * it before it had painted once, so it was seen as a flash and never as a loader. Reduced motion
 * skips both the hold and the fade: a still mark held on screen is a delay, not a loader.
 */
export function useBootHold(waiting: boolean): BootPhase {
  const [elapsed, setElapsed] = useState(() => prefersStill() || performance.now() >= BOOT_MIN_MS);
  const [phase, setPhase] = useState<BootPhase>(() => (elapsed && !waiting ? 'done' : 'holding'));

  useEffect(() => {
    if (elapsed) return;
    const t = window.setTimeout(() => setElapsed(true), Math.max(0, BOOT_MIN_MS - performance.now()));
    return () => window.clearTimeout(t);
  }, [elapsed]);

  useEffect(() => {
    const next = nextBootPhase(phase, { elapsed, waiting, still: prefersStill() });
    if (next !== phase) setPhase(next);
  }, [phase, elapsed, waiting]);

  useEffect(() => {
    if (phase !== 'leaving') return;
    const t = window.setTimeout(() => setPhase('done'), BOOT_FADE_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  return phase;
}

/**
 * The one step the hook does not time: `holding` becomes `leaving` (or `done`, for reduced motion,
 * which has nothing to fade) once the minimum has passed and the session is known. Nothing else
 * moves a phase on its own: `leaving` ends on its timer, and `done` is final.
 */
export function nextBootPhase(
  phase: BootPhase,
  now: { elapsed: boolean; waiting: boolean; still: boolean },
): BootPhase {
  if (phase !== 'holding' || !now.elapsed || now.waiting) return phase;
  return now.still ? 'done' : 'leaving';
}
